import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { startAnalysis, updateAnalysis, uploadProgress, analysisStep, markFresh } from './analysisStore';
import { FULL_MIX } from '../utils/instruments';
import { trackAnalysis } from '../utils/analytics';
import { readWaveform } from '../utils/clipWaveform';

// Analyses run for up to a couple of minutes; give up after five, like the server's proxy
export const ANALYSIS_TIMEOUT_MS = 300000;

// Draft clips are numbered for the visit: "new-1", "new-2", …
let draftCount = 0;
// Which real clip took each finished draft's place, so an address with the draft's id can follow it
const replacements = new Map();

/** Whether a clip id from the address is a draft's ("new-1") */
export const isDraftId = (id) => typeof id === 'string' && id.startsWith('new-');

/** The real clip id that took a finished draft's place, if it has finished */
export const draftReplacement = (draftId) => replacements.get(draftId);

/** Why a project or clip didn't load, in words: a missing one, a server message, or no connection */
export const loadErrorMessage = (err, what) => {
  if (err.status === 404) return `We couldn't find that ${what}.`;
  if (err.status) return err.message;
  return "We couldn't reach the server. Check your connection and try again.";
};

/**
 * POST a multipart form that starts an analysis, aborting after ANALYSIS_TIMEOUT_MS. onUploadProgress
 * gets (sent, total) bytes while the file uploads, and onEvent each step the server reports after
 * that (it's asked to stream them; a server that doesn't just answers at the end).
 */
export const postAnalysis = async (authFetch, path, fields, { onUploadProgress, onEvent } = {}) => {
  const body = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') body.append(key, value);
  });
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), ANALYSIS_TIMEOUT_MS);
  try {
    return await authFetch(`${path}?stream=1`, { method: 'POST', body, signal: controller.signal, onUploadProgress, onEvent });
  } catch (err) {
    if (err.name === 'AbortError') throw new Error('The analysis took too long. Please try again.');
    throw err;
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * A project and its clips (each a summary: name, tag, latest version and score), for the sidebar.
 * startClip creates a clip and analyzes its first version. onCredits gets the credits left after it.
 * rename and remove change the project itself.
 */
export const useProject = (projectId, { onCredits } = {}) => {
  const { authFetch } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const current = useRef(projectId);
  current.current = projectId;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await authFetch(`/projects/${projectId}`);
      if (current.current === projectId) setProject(data);
    } catch (err) {
      if (current.current === projectId) {
        setProject(null);
        setError(loadErrorMessage(err, 'project'));
      }
    } finally {
      if (current.current === projectId) setLoading(false);
    }
  }, [projectId, authFetch]);

  useEffect(() => {
    load();
  }, [load]);

  // A clip's summary changes after each analysis, rename or retag
  const upsertClip = useCallback((summary) => {
    setProject((prev) => {
      if (!prev) return prev;
      const exists = prev.clips.some((clip) => clip.id === summary.id);
      return {
        ...prev,
        clips: exists ? prev.clips.map((clip) => (clip.id === summary.id ? { ...clip, ...summary } : clip)) : [...prev.clips, summary],
      };
    });
  }, []);

  const removeClip = useCallback((clipId) => {
    setProject((prev) => prev && { ...prev, clips: prev.clips.filter((clip) => clip.id !== clipId) });
  }, []);

  /**
   * Create a clip and analyze its first version. A draft of the clip joins the list straight away
   * (its id is `draftId`, a string like "new-1") and is analyzing; when the analysis is done the real
   * clip takes its place, and if it fails the draft goes away. Returns { draftId, done }, where `done`
   * resolves to the API's { clip, version } or rejects with why it failed.
   */
  const startClip = useCallback(({ file, name, category, instrumentType, note }) => {
    draftCount += 1;
    const draftId = `new-${draftCount}`;
    const draft = {
      id: draftId, name, category, instrument_type: instrumentType || null,
      kind: category === FULL_MIX ? 'mix' : 'stem', latest_version: null, score: null, draft: true,
    };
    upsertClip(draft);

    const posted = postAnalysis(authFetch, `/projects/${projectId}/clips`, {
      file, name, category, instrument_type: instrumentType, note,
    }, { onUploadProgress: uploadProgress(draftId), onEvent: analysisStep(draftId) });
    const done = trackAnalysis(posted, { kind: draft.kind, category, version: 1 }).then(
      (result) => {
        // The real clip takes the draft's place in the list
        replacements.set(draftId, result.clip.id);
        markFresh(result.version.id);
        setProject((prev) => prev && { ...prev, clips: prev.clips.map((clip) => (clip.id === draftId ? result.clip : clip)) });
        if (result.credits_remaining !== undefined) onCredits?.(result.credits_remaining);
        return result;
      },
      (err) => {
        removeClip(draftId);
        throw err;
      }
    );
    startAnalysis(draftId, { filename: file.name, note, version: 1, startedAt: Date.now(), name, category, instrumentType }, done, { keep: false });
    readWaveform(file).then((waveform) => updateAnalysis(draftId, { waveform }));
    return { draftId, done };
  }, [authFetch, projectId, upsertClip, removeClip, onCredits]);

  const rename = useCallback(async (name) => {
    const updated = await authFetch(`/user/projects/${projectId}`, { method: 'PUT', json: { name } });
    setProject((prev) => prev && { ...prev, name: updated.name });
  }, [authFetch, projectId]);

  /** Delete the project and everything in it */
  const remove = useCallback(async () => {
    await authFetch(`/user/projects/${projectId}`, { method: 'DELETE' });
  }, [authFetch, projectId]);

  return { project, clips: project?.clips || [], loading, error, reload: load, upsertClip, removeClip, startClip, rename, remove };
};
